import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm, type FieldValues, type UseFormProps, type UseFormReturn } from 'react-hook-form';

/**
 * A void-returning submit handler.
 */
export type UseFormHandleSubmitSync<TFieldValues extends FieldValues, TTransformedValues = TFieldValues> = (
    onValid: SubmitHandler<TTransformedValues>,
    onInvalid?: SubmitErrorHandler<TFieldValues>,
) => (e?: React.BaseSyntheticEvent) => void;

/**
 * Hook output for {@link useFormExtended}.
 */
export type UseFormExtendedReturn<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
> = Omit<UseFormReturn<TFieldValues, TContext, TTransformedValues>, 'handleSubmit'> & {
    handleSubmit: UseFormHandleSubmitSync<TFieldValues, TTransformedValues>;
};

/**
 * This hook converts the types of {@link useForm} to make `handleSubmit` return
 * a void-returning handler, to avoid triggering lint rules for misused promises.
 */
export function useFormExtended<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
>(
    props?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
): UseFormExtendedReturn<TFieldValues, TContext, TTransformedValues> {
    return useForm(props);
}
